// Passkeys in the browser. The browser's own passkey prompt does the work; what goes
// in and out of it is the plain WebAuthn JSON that the Halo API's passkey
// endpoints and Cognito passkey sign-in exchange, which @simplewebauthn/browser
// converts to and from the browser's binary form.
import {
  base64URLStringToBuffer,
  browserSupportsWebAuthn,
  bufferToBase64URLString,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';

import * as passkeyApi from 'darwin_core/api/passkeys';
import { passkeyName, registrationRefused } from 'darwin_core/passkeys';

export function passkeysSupported() {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

// The user closed the browser's prompt, it timed out, or the browser had no
// passkey to offer: the browser deliberately reports these the same way, so
// none of them is an error worth reporting as a failure.
export function passkeyCancelled(error) {
  return (
    error?.name === 'NotAllowedError' ||
    error?.name === 'AbortError' ||
    error?.code === 'ERROR_CEREMONY_ABORTED'
  );
}

// This browser already holds a passkey for the account (the API lists the
// account's passkeys in the registration options so it can tell).
export function passkeyAlreadyAdded(error) {
  return error?.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';
}

// Most specific first: Edge and Opera also claim Chrome, every iOS browser and
// Chrome claim Safari, iPhones and iPads claim Mac OS X, and Android claims
// Linux. 'Edg' covers every Edge: Edg/ on desktop, EdgA/ on Android and EdgiOS/
// on iOS.
const BROWSERS = [
  ['Edg', 'Edge'],
  ['OPR/', 'Opera'],
  ['FxiOS/', 'Firefox'],
  ['Firefox/', 'Firefox'],
  ['CriOS/', 'Chrome'],
  ['Chrome/', 'Chrome'],
  ['Safari/', 'Safari'],
];
const SYSTEMS = [
  ['iPhone', 'iPhone'],
  ['iPad', 'iPad'],
  ['Android', 'Android'],
  ['CrOS', 'ChromeOS'],
  ['Mac OS X', 'Mac'],
  ['Windows', 'Windows'],
  ['Linux', 'Linux'],
];

const findIn = (userAgent, table) =>
  table.find(([marker]) => userAgent.includes(marker))?.[1];

// The name the passkey is listed under, e.g. "Chrome on Mac", so the list says
// where each one was added.
export function browserDeviceName(userAgent = globalThis.navigator?.userAgent) {
  const browser = findIn(userAgent || '', BROWSERS);
  const system = findIn(userAgent || '', SYSTEMS);
  if (browser && system) {
    return `${browser} on ${system}`;
  }
  return browser || system || 'Web browser';
}

// Asks the browser's password manager to drop a passkey the Halo API doesn't
// have. Best effort: only browsers with the WebAuthn Signal API can, and
// nothing is lost if one can't.
async function forgetUnregisteredPasskey(rpId, credentialId) {
  const signal = globalThis.PublicKeyCredential?.signalUnknownCredential;
  if (!signal || !rpId) {
    return;
  }
  try {
    await signal.call(globalThis.PublicKeyCredential, { rpId, credentialId });
  } catch {
    // See above.
  }
}

// Creates a passkey in this browser (or the password manager it uses) for the
// signed-in user and registers it with the Halo API.
export async function addPasskey() {
  const optionsJSON = await passkeyApi.getRegistrationOptions();
  const credential = await startRegistration({ optionsJSON });
  try {
    await passkeyApi.registerPasskey(
      passkeyName(browserDeviceName(), 'Web browser'),
      credential,
    );
  } catch (error) {
    // The passkey is already in the password manager. If the server refused
    // it, it would be offered at every passkey sign-in and always refused, so
    // drop it, as the app does. Not when the server may have saved it after
    // all (a timeout, say): that would leave a passkey the account lists but
    // this browser no longer has.
    if (registrationRefused(error)) {
      await forgetUnregisteredPasskey(optionsJSON.rp?.id, credential.id);
    }
    throw error;
  }
}

// Signs a Cognito passkey challenge (its WebAuthn request options) with a
// passkey the user picks in the browser's prompt.
export const answerPasskeyChallenge = (optionsJSON) =>
  startAuthentication({ optionsJSON });

// The Cognito username of the account a passkey belongs to, from the user
// handle the browser returns with its answer: the API makes each passkey with
// the account's sub, which is its Cognito username, as the WebAuthn user ID.
// WebAuthn requires a discoverable passkey to return it, but whether every
// password manager and phone does is unproven, so an answer without one fails
// as PasskeyWithoutUserHandle, saying what came back and from what kind of
// authenticator.
export function passkeyUsername(answer) {
  const handle = answer?.response?.userHandle;
  let username = '';
  if (handle) {
    try {
      username = new TextDecoder('utf-8', { fatal: true }).decode(
        base64URLStringToBuffer(handle),
      );
    } catch {
      // Reported below.
    }
  }
  if (!username) {
    const error = new Error(
      `The passkey's answer has ${handle ? 'an unreadable' : 'no'} user handle (authenticator: ${answer?.authenticatorAttachment ?? 'unknown'})`,
    );
    error.name = 'PasskeyWithoutUserHandle';
    throw error;
  }
  return username;
}

// Whether the browser can offer passkeys in a field's autofill (WebAuthn
// conditional mediation).
export async function passkeyAutofillSupported() {
  try {
    return (
      (await globalThis.PublicKeyCredential?.isConditionalMediationAvailable?.()) ===
      true
    );
  } catch {
    return false;
  }
}

// A sign-in challenge expires five minutes after the API issues it, so an
// autofill request is replaced with a fresh one before then. The margin
// leaves time for the user to finish with a passkey picked just before.
const AUTOFILL_CHALLENGE_LIFETIME = 4 * 60 * 1000;

// Closing the prompt after picking a passkey from the autofill ends the
// request, so it is offered again; but a browser that ends every request
// straight away would have it asking the API for challenges for as long as
// the page is open, so it stops after this many in a row.
const AUTOFILL_CANCELS_ALLOWED = 3;

// The API's sign-in options as the browser takes them for an autofill request.
function autofillRequestOptions(optionsJSON) {
  // No timeout: the request stays open for as long as the email step does,
  // and is replaced before its challenge expires (see above).
  const { timeout: _timeout, ...options } = optionsJSON;
  return {
    ...options,
    challenge: base64URLStringToBuffer(options.challenge),
    // Autofill needs an empty list; the API's options list none anyway.
    allowCredentials: [],
  };
}

// The browser's answer as the plain JSON startAuthentication gives for the
// prompt, which is what Cognito takes.
function answerJSON(credential) {
  const { response } = credential;
  return {
    id: credential.id,
    rawId: bufferToBase64URLString(credential.rawId),
    response: {
      authenticatorData: bufferToBase64URLString(response.authenticatorData),
      clientDataJSON: bufferToBase64URLString(response.clientDataJSON),
      signature: bufferToBase64URLString(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64URLString(response.userHandle)
        : undefined,
    },
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: ['platform', 'cross-platform'].includes(
      credential.authenticatorAttachment,
    )
      ? credential.authenticatorAttachment
      : undefined,
  };
}

// Offers the browser's Halo passkeys in the autofill of a field marked
// autocomplete="... webauthn", where the browser supports it, and calls
// onAnswer with the answer once the user picks one. Returns a function that
// stops it, which ends the browser's request at once: a browser has only one
// passkey request pending at a time, so it must be stopped before the prompt
// is opened, and when the field goes.
//
// This calls the browser directly rather than through startAuthentication:
// that can only be cancelled through a shared service, which would cancel
// whichever request is newest (the prompt's, say), and not one it hasn't
// started yet. A signal of its own ends exactly this request.
//
// It gives up quietly (in the console) if the browser or the API fails;
// stopping and offering again starts afresh.
export function offerPasskeyAutofill(onAnswer) {
  let stopped = false;
  let pending = null;

  async function offer() {
    let cancels = 0;
    while (!stopped) {
      const optionsJSON = await passkeyApi.getSignInOptions();
      if (stopped) {
        return;
      }
      const controller = new AbortController();
      pending = controller;
      const refresh = setTimeout(
        () => controller.abort(),
        AUTOFILL_CHALLENGE_LIFETIME,
      );
      let credential;
      try {
        credential = await navigator.credentials.get({
          mediation: 'conditional',
          publicKey: autofillRequestOptions(optionsJSON),
          signal: controller.signal,
        });
      } catch (error) {
        if (stopped) {
          return;
        }
        if (controller.signal.aborted) {
          // Replaced before its challenge expires.
          cancels = 0;
          continue;
        }
        if (
          error?.name === 'NotAllowedError' &&
          ++cancels < AUTOFILL_CANCELS_ALLOWED
        ) {
          continue;
        }
        throw error;
      } finally {
        clearTimeout(refresh);
      }
      if (stopped) {
        return;
      }
      if (!credential) {
        throw new Error('The browser returned no passkey');
      }
      onAnswer(answerJSON(credential));
      return;
    }
  }

  (async () => {
    try {
      if ((await passkeyAutofillSupported()) && !stopped) {
        await offer();
      }
    } catch (error) {
      console.warn('Passkey autofill stopped:', error?.name, error?.message);
    }
  })();

  return () => {
    stopped = true;
    pending?.abort();
  };
}
